import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSession } from "@/context/SessionContext";
import { useLookup, lookupLabel } from "@/lib/lookups";
import { cn } from "@/lib/utils";

const NONE = "__none__";

export const getPath = (o, p) => p.split(".").reduce((a, k) => (a == null ? undefined : a[k]), o);
export const setPath = (o, p, v) => {
  const ks = p.split(".");
  const root = { ...o };
  let cur = root;
  ks.slice(0, -1).forEach((k) => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
  cur[ks[ks.length - 1]] = v;
  return root;
};

const tid = (name) => `field-${name.replace(/\./g, "-").replace(/_/g, "-")}`;

const SimpleSelect = ({ value, onChange, options, required, disabled, testId, placeholder = "Not set" }) => (
  <Select value={value === null || value === undefined || value === "" ? NONE : String(value)} onValueChange={(v) => onChange(v === NONE ? null : options.find((o) => String(o.value) === v)?.value ?? v)} disabled={disabled}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {!required && <SelectItem value={NONE}>{placeholder}</SelectItem>}
      {required && (value === null || value === undefined) && <SelectItem value={NONE} disabled>Select…</SelectItem>}
      {options.map((o) => <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Chips = ({ value = [], options, onChange, disabled, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {options.map((o) => {
      const on = value.includes(o.value);
      return (
        <button type="button" key={o.value} disabled={disabled} onClick={() => onChange(on ? value.filter((x) => x !== o.value) : [...value, o.value])}
          className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors", on ? "bg-[#18191D] text-white border-[#18191D]" : "bg-white hover:border-[hsl(var(--gold))]")}
          data-testid={`${testId}-${o.value}`}>{o.label}</button>
      );
    })}
  </div>
);

const RefInput = ({ kind, value, onChange, multi, disabled, testId }) => {
  const { items, allowed } = useLookup(kind);
  if (!allowed) return <div className="text-xs text-muted-foreground py-2">No access to {kind}</div>;
  const options = items.map((d) => ({ value: d.id, label: lookupLabel(kind, d) }));
  return multi
    ? <Chips value={value || []} options={options} onChange={onChange} disabled={disabled} testId={testId} />
    : <SimpleSelect value={value} onChange={onChange} options={options} disabled={disabled} testId={testId} placeholder="None" />;
};

const ContactsEditor = ({ value = [], onChange, disabled }) => {
  const upd = (i, k, v) => onChange(value.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  return (
    <div className="space-y-2" data-testid="field-contacts">
      {value.map((c, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-[1.2fr_1fr_1.2fr_1fr_auto_auto] gap-2 items-center border rounded-md p-2">
          <Input placeholder="Name *" value={c.name || ""} onChange={(e) => upd(i, "name", e.target.value)} disabled={disabled} data-testid={`contact-${i}-name`} />
          <Input placeholder="Phone" value={c.phone || ""} onChange={(e) => upd(i, "phone", e.target.value)} disabled={disabled} data-testid={`contact-${i}-phone`} />
          <Input placeholder="Email" value={c.email || ""} onChange={(e) => upd(i, "email", e.target.value)} disabled={disabled} />
          <Input placeholder="Designation" value={c.designation || ""} onChange={(e) => upd(i, "designation", e.target.value)} disabled={disabled} />
          <label className="flex items-center gap-1.5 text-xs whitespace-nowrap"><Checkbox checked={!!c.is_authorized_rep} onCheckedChange={(v) => upd(i, "is_authorized_rep", !!v)} disabled={disabled} />Authorized rep</label>
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))} disabled={disabled}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...value, { name: "", is_authorized_rep: false }])} disabled={disabled} data-testid="add-contact-button"><Plus className="h-3.5 w-3.5 mr-1" />Add contact</Button>
    </div>
  );
};

export const FieldInput = ({ field, value, onChange, disabled }) => {
  const { branches } = useSession();
  const testId = tid(field.name);
  const branchOpts = branches.filter((b) => b.active || b.id === value).map((b) => ({ value: b.id, label: `${b.name} (${b.code})` }));
  switch (field.type) {
    case "number":
      return <Input type="number" step="any" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testId} />;
    case "datetime":
      return <Input type="datetime-local" value={(value || "").slice(0, 16)} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} />;
    case "date":
      return <Input type="date" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} />;
    case "textarea":
      return <Textarea rows={3} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
    case "switch":
      return <div className="pt-1.5"><Switch checked={!!value} disabled={disabled} onCheckedChange={onChange} data-testid={testId} /></div>;
    case "select":
      return <SimpleSelect value={value} onChange={onChange} options={field.options} required={field.required} disabled={disabled} testId={testId} />;
    case "multiselect":
      return <Chips value={value || []} options={field.options} onChange={onChange} disabled={disabled} testId={testId} />;
    case "tags":
      return <Input value={(value || []).join(", ")} disabled={disabled} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trimStart()))} data-testid={testId} />;
    case "branch":
      return <SimpleSelect value={value} onChange={onChange} options={branchOpts} required={field.required} disabled={disabled} testId={testId} />;
    case "branches":
      return <Chips value={value || []} options={branchOpts} onChange={onChange} disabled={disabled} testId={testId} />;
    case "ref":
      return <RefInput kind={field.ref} value={value} onChange={onChange} disabled={disabled} testId={testId} />;
    case "refmulti":
      return <RefInput kind={field.ref} value={value} onChange={onChange} multi disabled={disabled} testId={testId} />;
    case "contacts":
      return <ContactsEditor value={value || []} onChange={onChange} disabled={disabled} />;
    default:
      return <Input value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
  }
};
